import React, { createContext, useCallback, useContext, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { type TabId } from "../constants/scribeUi";
import { RECORD_TAB_ID, getVisibleTabSpecs, type TabSpec } from "../constants/scribeTabs";

export interface ScribeNavValue {
  /** Tools to show, in order (SCRIBE_VISIBLE_TABS). */
  visibleTabs: TabSpec[];
  /** The active tool; falls back to the first visible one if the selection is not in the list. */
  activeTab: TabId;
  /**
   * Asks to change tool. Leaving Record mid-recording opens the confirmation dialog first;
   * the recording itself survives either way because the Record panel stays mounted.
   */
  requestTabChange: (next: TabId) => void;
  /** Changes tool without asking (programmatic moves, such as arriving from another page). */
  selectTab: (next: TabId) => void;
  /** True while the Record tool reports a live recording. */
  isRecording: boolean;
  setIsRecording: (active: boolean) => void;
}

const ScribeNavContext = createContext<ScribeNavValue | null>(null);

export const useScribeNav = (): ScribeNavValue => {
  const value = useContext(ScribeNavContext);
  if (!value) throw new Error("useScribeNav must be used inside ScribeNavProvider");
  return value;
};

/** Shares the active Scribe tool between the navigation (rail or bottom bar) and ScribeMain. */
export const ScribeNavProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const visibleTabs = getVisibleTabSpecs();
  const [selectedTab, setSelectedTab] = useState<TabId>(() => visibleTabs[0]?.id ?? "record");
  const [isRecording, setIsRecording] = useState(false);
  const [pendingTab, setPendingTab] = useState<TabId | null>(null);

  const activeTab: TabId = visibleTabs.some((t) => t.id === selectedTab)
    ? selectedTab
    : (visibleTabs[0]?.id ?? selectedTab);

  const requestTabChange = useCallback(
    (next: TabId) => {
      if (next === activeTab) return;
      if (isRecording && activeTab === RECORD_TAB_ID) {
        setPendingTab(next);
        return;
      }
      setSelectedTab(next);
    },
    [activeTab, isRecording],
  );

  const handleContinueRecording = useCallback(() => setPendingTab(null), []);

  const handleConfirmSwitch = useCallback(() => {
    if (pendingTab !== null) setSelectedTab(pendingTab);
    setPendingTab(null);
  }, [pendingTab]);

  // Rebuilt each render: the provider re-renders only on a tool or recording change.
  const value: ScribeNavValue = {
    visibleTabs,
    activeTab,
    requestTabChange,
    selectTab: setSelectedTab,
    isRecording,
    setIsRecording,
  };

  return (
    <ScribeNavContext.Provider value={value}>
      {children}
      <Dialog open={pendingTab !== null} onClose={handleContinueRecording} aria-labelledby="recording-in-progress-title">
        <DialogTitle id="recording-in-progress-title">Recording in progress</DialogTitle>
        <DialogContent>
          <DialogContentText>
            A recording is currently in progress. It will keep recording while you use another tab. Do you want to
            switch?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleContinueRecording} autoFocus>
            Continue Recording
          </Button>
          <Button onClick={handleConfirmSwitch}>Switch tab</Button>
        </DialogActions>
      </Dialog>
    </ScribeNavContext.Provider>
  );
};
